import { useCallback } from 'react';

import { useShallow } from 'zustand/react/shallow';

import { useRoomContext } from '@src/contexts';
import SocketService from '@src/services/SocketService';
import { useCalculatorStore, useNotificationStore, usePeersStore } from '@src/stores';

import {
  ECalculatorSocketEvents,
  IShareCalculatorParams,
  IStopCalculatorParams,
  IStopSharingCalculatorPayload,
} from '@src/types';

export const useCalculatorActions = () => {
  const { isWebinar } = useRoomContext();

  const {
    resetSharedCalculatorData,
    setIsPersonalCalculatorVisible,
    setSharedCalculatorData,
    targetPeerId,
    isSharedByMe,
  } = useCalculatorStore(
    useShallow((state) => ({
      resetSharedCalculatorData: state.resetSharedCalculatorData,
      setIsPersonalCalculatorVisible: state.setIsPersonalCalculatorVisible,
      setSharedCalculatorData: state.setSharedCalculatorData,
      targetPeerId: state.sharedCalculatorData.owner?.peer_id,
      isSharedByMe: state.sharedCalculatorData.isSharedByMe,
    }))
  );

  const { appendNotification } = useNotificationStore(
    useShallow((state) => ({
      appendNotification: state.appendNotification,
    }))
  );

  const stopCalculatorSharing = useCallback(
    async ({
      targetPeerId,
      targetRecordId,
      forceStoppedByHost = false,
      closePersonalCalculator = false,
    }: IStopCalculatorParams) => {
      try {
        let payload: IStopSharingCalculatorPayload | undefined;

        if (isWebinar) {
          const derivedRecordId = targetPeerId
            ? usePeersStore.getState().peers.get(targetPeerId)?.record_id
            : undefined;

          if (derivedRecordId) {
            payload = {
              forceStoppedByHost,
              targetRecordId: derivedRecordId,
            };
          }
        } else {
          if (targetPeerId) {
            payload = {
              forceStoppedByHost,
              targetPeerId,
            };
          }
        }

        const response: { success: boolean } = await SocketService.request(
          ECalculatorSocketEvents.STOP_SHARING_CALCULATOR,
          payload
        );

        if (response.success) {
          resetSharedCalculatorData();

          if (closePersonalCalculator) {
            setIsPersonalCalculatorVisible(false);
          }

          const derivedRecordId =
            !targetRecordId && targetPeerId ? usePeersStore.getState().peers.get(targetPeerId)?.record_id : undefined;
          const didTargetSpecificUser = isWebinar ? !!derivedRecordId : !!targetPeerId;

          const notificationText = didTargetSpecificUser
            ? 'Calculator sharing stopped.'
            : 'Calculator hidden from participants.';

          appendNotification({
            text: notificationText,
            source: 'calculator',
          });

          return true;
        }

        return false;
      } catch (err) {
        return false;
      }
    },
    [appendNotification, isWebinar, resetSharedCalculatorData, setIsPersonalCalculatorVisible]
  );

  const handleShareCalculator = useCallback(
    async ({ mode, currentPeer }: IShareCalculatorParams) => {
      try {
        const response: { success: boolean } = await SocketService.request(ECalculatorSocketEvents.SHARE_CALCULATOR, {
          calculatorDetails: { mode },
        });

        if (response.success) {
          setSharedCalculatorData({
            isOpen: true,
            isSharedByMe: true,
            owner: {
              peer_name: currentPeer.peer_name,
              peer_id: currentPeer.peer_id,
              peer_uuid: currentPeer.peer_uuid,
            },
            settings: { mode },
          });

          appendNotification({
            text: 'Calculator is now visible to participants.',
            source: 'calculator',
          });

          return true;
        }

        return false;
      } catch (err) {
        resetSharedCalculatorData();

        return false;
      }
    },
    [appendNotification, resetSharedCalculatorData, setSharedCalculatorData]
  );

  const handleStopShareCalculator = useCallback(() => {
    return stopCalculatorSharing({ closePersonalCalculator: false });
  }, [stopCalculatorSharing]);

  const handleStopSharing = useCallback(async () => {
    try {
      if (isSharedByMe) {
        await handleStopShareCalculator();
      } else if (targetPeerId) {
        await stopCalculatorSharing({
          targetPeerId,
          forceStoppedByHost: true,
        });
      }
    } catch (error) {
      console.error('Failed to stop sharing:', error);
    }
  }, [isSharedByMe, targetPeerId, handleStopShareCalculator, stopCalculatorSharing]);

  return {
    handleShareCalculator,
    handleStopShareCalculator,
    handleStopSharing,
    stopCalculatorSharing,
  };
};

export default useCalculatorActions;
